import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Video, Check, X, Clock, Repeat, AlertTriangle } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { errMsg, localeFor, MOVABLE_BOOKING_STATUSES } from "./calendarUtils";
import RescheduleDialog from "./RescheduleDialog";
import { formatMoneyIntl } from "../../lib/formatMoney";
import { adminHref } from "../adminPath";

const fmtPrice = (cents, currency, lang) => formatMoneyIntl(cents, currency, lang);

/**
 * One APPOINTMENT, opened from the calendar (a booking event, or the mismatch pill).
 *
 * `booking` is the row from GET /admin/bookings (or, for the mismatch pill when the
 * calendar has not loaded it, the server's mismatch projection of the same row). The
 * booking is the authority for itself: its service, start and booked length were stamped
 * at creation (`service_id`/`service`, `slot_start`, `slot_duration_min`), so this dialog
 * reads them and never re-derives them from the catalogue or the opening hours. The
 * catalogue is read only for the service's advertised price line.
 *
 * There is nothing else to edit here: an appointment's length is its service's, the
 * opening hours are edited on the Availability screen, and moving it is the Reschedule
 * dialog's (or the calendar drag's) job.
 */
export const EventDetailDialog = ({ booking: b, onClose, onChanged }) => {
  const t = useT("admin");
  const { lang } = useLanguage();
  const isDrift = b.sync_status === "drift" && !!b.gcal_drift;
  const isGoogleCancelled = !!b.needs_client_notice;
  const start = new Date(b.slot_start);
  const buffer = Number(b.buffer_after_min) || 0;
  const [syncBusy, setSyncBusy] = useState(false);
  const [services, setServices] = useState([]);
  useEffect(() => {
    if (!b.service_id) return;
    api.get("/booking/services").then(({ data }) => setServices(data || [])).catch(() => setServices([]));
  }, [b.service_id]);
  const bookedService = services.find((s) => s.id === b.service_id);
  // Double-click guard: two concurrent status PUTs used to release the booking's time
  // twice server-side (fixed server-side, kept here as well).
  const [actBusy, setActBusy] = useState(false);
  // N8 — the practitioner's own move to another time. Rendered as a second, stacked
  // dialog; on success it also closes THIS one, same as every other action below
  // (setStatus/repush/acknowledge all call onChanged(); onClose()).
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const repush = async () => {
    setSyncBusy(true);
    try { await api.post("/tenant/calendar/sync"); toast.success(t("sync.repushDone")); onChanged(); onClose(); }
    catch (e) { toast.error(errMsg(e, t, "gcal.errorToast")); }
    finally { setSyncBusy(false); }
  };
  const acknowledge = async () => {
    setSyncBusy(true);
    try { await api.post(`/admin/bookings/${b.id}/mismatch-ack`); toast.success(t("sync.ackDone")); onChanged(); onClose(); }
    catch (e) { toast.error(errMsg(e, t)); }
    finally { setSyncBusy(false); }
  };
  const setStatus = async (status) => {
    if (actBusy) return;
    setActBusy(true);
    try { await api.put(`/admin/bookings/${b.id}`, { status }); toast.success(t("cal.updated")); onChanged(); onClose(); }
    catch (e) { toast.error(errMsg(e, t)); }
    finally { setActBusy(false); }
  };
  // Telehealth is per-minute consults and nothing else, so there is no hand-opened room
  // to mint here any more (`POST /telehealth/rooms` is gone, and a fixed-price booking is
  // `in_person` by product nature). A per-minute booking's room already exists by the
  // time this dialog can show it — it is written when the authorisation intent is
  // created. Same routing as BookingsAdmin.jsx's row action: the practitioner's door is
  // OUR OWN room page (admin/telehealth/ConsultRoomPage.jsx), which hosts the call and
  // heartbeats for the whole visit, never a bare 302 to the provider.
  // DELIVERY, not billing — see the same gate in `BookingsAdmin.jsx`. Both telehealth
  // models carry `delivery_mode: "video"`, and only that field means "there is a room".
  const isVideoConsult = b.delivery_mode === "video";
  const openConsultRoom = () => {
    window.open(adminHref(`/admin/telehealth/consult/${b.id}`), "_blank");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cal-detail-dialog">
        <DialogHeader>
          <DialogTitle>{b.service}</DialogTitle>
          <DialogDescription className="sr-only">{t("cal.detailDialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-eh-muted" data-testid="detail-when">
            <Clock className="w-4 h-4" />
            {start.toLocaleString(localeFor(lang), { dateStyle: "full", timeStyle: "short" })} · {b.slot_duration_min} min
            {buffer > 0 && <span className="text-xs" data-testid="detail-buffer">{t("cal.bufferAfter", { n: buffer })}</span>}
          </div>
          {bookedService && (
            <div className="flex items-center gap-2 text-xs" data-testid="detail-service-price">
              <span className="text-eh-muted">{bookedService.name}</span>
              <span className="font-medium text-eh-ink">
                {bookedService.price_cents > 0 ? fmtPrice(bookedService.price_cents, bookedService.currency, lang) : t("cal.priceFree")}
              </span>
            </div>
          )}
          {isDrift && (
            <div className="bg-eh-ochre/15 border border-eh-ochre-deep/40 rounded-lg p-3 text-xs" data-testid="detail-drift-banner">
              <div className="flex items-center gap-1.5 text-eh-ochre-deep font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {t("sync.driftBanner")}</div>
              <div className="mt-1.5 space-y-0.5 text-eh-muted">
                <div>{t("sync.driftGoogleTime", { time: b.gcal_drift?.start ? new Date(b.gcal_drift.start).toLocaleString(localeFor(lang), { dateStyle: "medium", timeStyle: "short" }) : "—" })}</div>
                <div>{t("sync.driftSolunaryTime", { time: start.toLocaleString(localeFor(lang), { dateStyle: "medium", timeStyle: "short" }) })}</div>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={syncBusy} onClick={repush} data-testid="detail-repush">{t("sync.repush")}</Button>
                <Button size="sm" variant="outline" disabled={syncBusy} onClick={acknowledge} data-testid="detail-ack-drift">{t("sync.acknowledge")}</Button>
              </div>
            </div>
          )}
          {isGoogleCancelled && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-xs" data-testid="detail-google-cancelled-banner">
              <div className="flex items-center gap-1.5 text-red-700 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {t("sync.googleCancelledBanner")}</div>
              <div className="mt-2">
                <Button size="sm" variant="outline" disabled={syncBusy} onClick={acknowledge} data-testid="detail-ack-notice">{t("sync.acknowledge")}</Button>
              </div>
            </div>
          )}
          <div className="bg-eh-cream/40 rounded-lg p-3">
            <div className="font-medium text-eh-ink">{b.firstName} {b.lastName}</div>
            <div className="text-eh-muted text-xs break-all">{b.email}{b.phone ? ` · ${b.phone}` : ""}</div>
            {b.notes && <div className="text-eh-muted text-xs mt-1">{b.notes}</div>}
            <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-white">{t(`bookings.statusLabel.${b.status}`, null, b.status)}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {isVideoConsult && <Button size="sm" onClick={openConsultRoom} data-testid="detail-video" className="bg-sol-gold text-eh-ink hover:bg-sol-gold/90"><Video className="w-4 h-4 mr-1" /> {t("cal.video")}</Button>}
            {/* B3 — same condition as BookingsAdmin: confirming a seat hold
                (pending_payment) used to mint a FREE appointment whose money was never
                collected; paid_needs_attention is resolved from BookingsAdmin (the
                Keep/Refund banner), not from this generic button. */}
            {["pending", "cancelled"].includes(b.status) && <Button size="sm" variant="outline" disabled={actBusy} onClick={() => setStatus("confirmed")} data-testid="detail-confirm"><Check className="w-4 h-4 mr-1" /> {t("bookings.confirm")}</Button>}
            {/* N8 — the server's own movable states (calendarUtils.MOVABLE_BOOKING_STATUSES):
                a booking with no live time to move never gets this button. */}
            {MOVABLE_BOOKING_STATUSES.includes(b.status) && <Button size="sm" variant="outline" disabled={actBusy} onClick={() => setRescheduleOpen(true)} data-testid="detail-reschedule"><Repeat className="w-4 h-4 mr-1" /> {t("bookings.reschedule")}</Button>}
            {b.status !== "cancelled" && <Button size="sm" variant="outline" disabled={actBusy} onClick={() => setStatus("cancelled")} data-testid="detail-cancel"><X className="w-4 h-4 mr-1" /> {t("bookings.cancel")}</Button>}
          </div>
        </div>
      </DialogContent>

      {rescheduleOpen && (
        <RescheduleDialog
          booking={b}
          onClose={() => setRescheduleOpen(false)}
          onChanged={() => { onChanged(); onClose(); }}
        />
      )}
    </Dialog>
  );
};
